import { useState, useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import ReorderIcon from '@mui/icons-material/Reorder';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    useProductCatalogsQuery,
    useUpdateProductCatalogOrderMutation,
} from '@features/redux/apis/productCatalog';
import { useAlert } from '@hooks/useAlert';
import {
    colors,
    typography as typographyTokens,
    radii,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
} from '@components/design-system';
import type { ProductCatalog } from '@/types/productCatalog';

interface OrderProductCatalogsPanelProps {
    onClose: () => void;
}

type ProductsByLine = Record<string, ProductCatalog[]>;

const PRODUCT_LINE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    Original: { bg: colors.primary[50], text: colors.primary[700], border: colors.primary[200] },
    Fusion: { bg: colors.success.light, text: colors.success.dark, border: '#A7F3D0' },
    'Style Skins': { bg: colors.warning.light, text: colors.warning.dark, border: '#FDE68A' },
};

const PRODUCT_LINE_ORDER = ['Original', 'Fusion', 'Style Skins'];

const groupByProductLine = (products: ProductCatalog[]): ProductsByLine =>
    products.reduce<ProductsByLine>((acc, product) => {
        const line = product.ProductLine;
        return {
            ...acc,
            [line]: [...(acc[line] || []), product].sort((a, b) => a.SortOrder - b.SortOrder),
        };
    }, {});

export const OrderProductCatalogsPanel = ({ onClose }: OrderProductCatalogsPanelProps) => {
    const { setAlert } = useAlert();

    const { data: productCatalogs = [], isLoading: isLoadingProducts } = useProductCatalogsQuery({});
    const [updateOrder, { isLoading: isUpdating }] = useUpdateProductCatalogOrderMutation();

    const [localProducts, setLocalProducts] = useState<ProductsByLine>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [draggedItem, setDraggedItem] = useState<{ line: string; index: number } | null>(null);
    const [expandedLines, setExpandedLines] = useState<Record<string, boolean>>({});

    // Initialize local state when products load
    useMemo(() => {
        if (productCatalogs.length > 0 && Object.keys(localProducts).length === 0) {
            const grouped = groupByProductLine(productCatalogs);
            setLocalProducts(grouped);
            // Expand all lines by default
            const expanded = Object.keys(grouped).reduce((acc, line) => ({ ...acc, [line]: true }), {});
            setExpandedLines(expanded);
        }
    }, [productCatalogs]);

    const productLines = useMemo(
        () =>
            Object.keys(localProducts).sort((a, b) => {
                const indexA = PRODUCT_LINE_ORDER.indexOf(a);
                const indexB = PRODUCT_LINE_ORDER.indexOf(b);
                const sortA = indexA === -1 ? Infinity : indexA;
                const sortB = indexB === -1 ? Infinity : indexB;
                return sortA - sortB;
            }),
        [localProducts]
    );

    const moveItem = useCallback((line: string, fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        setLocalProducts((prev) => {
            const lineProducts = [...prev[line]];
            const [removed] = lineProducts.splice(fromIndex, 1);
            lineProducts.splice(toIndex, 0, removed);

            return { ...prev, [line]: lineProducts };
        });
        setHasChanges(true);
    }, []);

    const handleDragStart = (line: string, index: number) => {
        setDraggedItem({ line, index });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (line: string, targetIndex: number) => {
        if (draggedItem && draggedItem.line === line) {
            moveItem(line, draggedItem.index, targetIndex);
        }
        setDraggedItem(null);
    };

    const handleMoveUp = (line: string, index: number) => {
        if (index > 0) {
            moveItem(line, index, index - 1);
        }
    };

    const handleMoveDown = (line: string, index: number) => {
        if (index < localProducts[line].length - 1) {
            moveItem(line, index, index + 1);
        }
    };

    const toggleLine = (line: string) => {
        setExpandedLines((prev) => ({ ...prev, [line]: !prev[line] }));
    };

    const handleSave = async () => {
        const orders = productLines.flatMap((line) =>
            localProducts[line].map((product, index) => ({
                ProductSKU: product.ProductSKU,
                SortOrder: index,
            }))
        );

        try {
            await updateOrder({ orders }).unwrap();
            setAlert('Product order updated successfully!', 'success', 5000);
            setHasChanges(false);
            onClose();
        } catch {
            setAlert('Failed to update product order. Please try again.', 'error', 5000);
        }
    };

    return (
        <EntityPageLayout loading={isLoadingProducts}>
            <EntityPageHeader
                entityType='system'
                title='Manage Product Order'
                icon={<ReorderIcon />}
                isCreate={false}
                loading={isLoadingProducts}
                onCancel={onClose}
                onSave={handleSave}
                saveDisabled={!hasChanges}
                saveText='Save Order'
                saving={isUpdating}
            />

            <InfoCard
                title='Product Display Order'
                icon={<InfoOutlinedIcon />}
                note='Drag items or use arrows to reorder products within each product line. Products are displayed in this order on the kiosk.'>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {productLines.map((line) => {
                        const lineColor = PRODUCT_LINE_COLORS[line] || {
                            bg: colors.neutral[100],
                            text: colors.neutral[700],
                            border: colors.neutral[300],
                        };
                        const isExpanded = expandedLines[line] ?? true;

                        return (
                            <Box key={line}>
                                {/* Product Line Header */}
                                <Box
                                    onClick={() => toggleLine(line)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        backgroundColor: lineColor.bg,
                                        borderRadius: `${radii.md}px`,
                                        borderLeft: `4px solid ${lineColor.border}`,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.9,
                                        },
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.base,
                                                fontWeight: typographyTokens.fontWeight.semibold,
                                                color: lineColor.text,
                                            }}>
                                            {line}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                color: lineColor.text,
                                                opacity: 0.7,
                                            }}>
                                            ({localProducts[line].length} products)
                                        </Typography>
                                    </Box>
                                    <IconButton size='small' sx={{ color: lineColor.text }}>
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>

                                {/* Product Items */}
                                {isExpanded && (
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {localProducts[line].map((product, index) => (
                                            <Paper
                                                key={product.ProductSKU}
                                                elevation={0}
                                                draggable
                                                onDragStart={() => handleDragStart(line, index)}
                                                onDragOver={handleDragOver}
                                                onDrop={() => handleDrop(line, index)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 1.5,
                                                    ml: 2,
                                                    borderRadius: `${radii.md}px`,
                                                    backgroundColor:
                                                        draggedItem?.line === line && draggedItem?.index === index
                                                            ? colors.primary[50]
                                                            : colors.neutral[0],
                                                    border: `1px solid ${
                                                        draggedItem?.line === line && draggedItem?.index === index
                                                            ? colors.primary[300]
                                                            : colors.neutral[200]
                                                    }`,
                                                    cursor: 'grab',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        backgroundColor: colors.neutral[50],
                                                        borderColor: colors.neutral[300],
                                                    },
                                                    '&:active': {
                                                        cursor: 'grabbing',
                                                    },
                                                }}>
                                                <DragIndicatorIcon
                                                    sx={{ color: colors.neutral[400], mr: 1.5, fontSize: 20 }}
                                                />
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: `${radii.full}px`,
                                                        backgroundColor: colors.neutral[900],
                                                        color: colors.neutral[0],
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: typographyTokens.fontSize.xs,
                                                        fontWeight: typographyTokens.fontWeight.semibold,
                                                        mr: 1.5,
                                                    }}>
                                                    {index + 1}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        px: 1,
                                                        py: 0.25,
                                                        mr: 1.5,
                                                        borderRadius: `${radii.sm}px`,
                                                        backgroundColor: colors.neutral[100],
                                                        fontFamily: typographyTokens.fontFamily.mono,
                                                        fontSize: typographyTokens.fontSize.xs,
                                                        fontWeight: typographyTokens.fontWeight.semibold,
                                                        color: colors.neutral[700],
                                                        letterSpacing: '0.05em',
                                                    }}>
                                                    {product.ShortCode}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: typographyTokens.fontSize.sm,
                                                            fontWeight: typographyTokens.fontWeight.medium,
                                                            color: colors.neutral[900],
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {product.ProductName}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: typographyTokens.fontSize.xs,
                                                            color: colors.neutral[500],
                                                            fontFamily: typographyTokens.fontFamily.mono,
                                                        }}>
                                                        {product.ProductSKU}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton
                                                        size='small'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMoveUp(line, index);
                                                        }}
                                                        disabled={index === 0}
                                                        sx={{
                                                            color: index === 0 ? colors.neutral[300] : colors.neutral[500],
                                                            '&:hover': {
                                                                backgroundColor: colors.neutral[100],
                                                            },
                                                        }}>
                                                        <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size='small'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMoveDown(line, index);
                                                        }}
                                                        disabled={index === localProducts[line].length - 1}
                                                        sx={{
                                                            color:
                                                                index === localProducts[line].length - 1
                                                                    ? colors.neutral[300]
                                                                    : colors.neutral[500],
                                                            '&:hover': {
                                                                backgroundColor: colors.neutral[100],
                                                            },
                                                        }}>
                                                        <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </InfoCard>
        </EntityPageLayout>
    );
};

// Keep old export names for backwards compatibility
export const OrderMaterialProductsPanel = OrderProductCatalogsPanel;
export const OrderMaterialProductsDialog = OrderProductCatalogsPanel;
